import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getPolicies, getPolicy, createPolicy, updatePolicy, getPolicyRevisions, comparePolicyRevisions, restorePolicyVersion } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Plus, Search, FileText, ChevronLeft, CheckCircle, Eye, Edit, History,
  Clock, User, GitCompare, RotateCcw, AlertTriangle, ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const STATUS_COLORS = {
  draft: 'bg-slate-800 text-slate-400 border-slate-700',
  pending_approval: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50',
  approved: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50',
  archived: 'bg-slate-800 text-slate-500 border-slate-700',
};

const CHANGE_TYPE_COLORS = {
  created: 'bg-blue-500/20 text-blue-400',
  updated: 'bg-yellow-500/20 text-yellow-400',
  status_change: 'bg-violet-500/20 text-violet-400',
  approved: 'bg-emerald-500/20 text-emerald-400',
  archived: 'bg-slate-500/20 text-slate-400',
  restored: 'bg-cyan-500/20 text-cyan-400',
};

export default function PoliciesPage({ initialMode }) {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(initialMode || 'list');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('content');
  
  // Revision state
  const [revisions, setRevisions] = useState([]);
  const [selectedRevisions, setSelectedRevisions] = useState([]);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreVersion, setRestoreVersion] = useState(null);
  const [restoreReason, setRestoreReason] = useState('');

  const [formData, setFormData] = useState({ 
    title: '', content: '', category: 'policy', regulatory_references: [], 
    change_reason: '' 
  });

  useEffect(() => { if (mode === 'list') fetchPolicies(); }, [mode]);

  const fetchPolicies = async () => {
    setLoading(true);
    try { const response = await getPolicies(api); setPolicies(response.data); }
    catch (error) { console.error('Failed to fetch policies:', error); }
    finally { setLoading(false); }
  };

  const fetchRevisions = async (policyId) => {
    try {
      const response = await getPolicyRevisions(api, policyId);
      setRevisions(response.data.revisions || []);
    } catch (error) {
      console.error('Failed to fetch revisions:', error);
    }
  };

  const handleView = async (policy) => {
    setSelectedPolicy(policy);
    setMode('view');
    setActiveTab('content');
    fetchRevisions(policy.id);
  };

  const handleEdit = (policy) => {
    setSelectedPolicy(policy);
    setFormData({
      title: policy.title || '',
      content: policy.content || '',
      category: policy.category || 'policy',
      regulatory_references: policy.regulatory_references || [],
      change_reason: ''
    });
    setMode('edit');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try { 
      await createPolicy(api, formData); 
      toast.success('Policy created successfully');
      setMode('list'); 
      setFormData({ title: '', content: '', category: 'policy', regulatory_references: [], change_reason: '' }); 
    }
    catch (error) { 
      console.error('Failed to create policy:', error); 
      toast.error('Failed to create policy');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try { 
      await updatePolicy(api, selectedPolicy.id, formData); 
      toast.success('Policy updated successfully');
      setMode('list'); 
      setSelectedPolicy(null);
      setFormData({ title: '', content: '', category: 'policy', regulatory_references: [], change_reason: '' }); 
    }
    catch (error) { 
      console.error('Failed to update policy:', error); 
      toast.error('Failed to update policy');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updatePolicy(api, selectedPolicy.id, { status: newStatus, change_reason: `Status changed to ${newStatus}` });
      setSelectedPolicy({ ...selectedPolicy, status: newStatus });
      fetchRevisions(selectedPolicy.id);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleCompare = async () => {
    if (selectedRevisions.length !== 2) return;
    try {
      const [v1, v2] = selectedRevisions.sort((a, b) => a - b);
      const response = await comparePolicyRevisions(api, selectedPolicy.id, v1, v2);
      setComparisonResult(response.data);
      setShowCompareDialog(true);
    } catch (error) {
      console.error('Failed to compare revisions:', error);
      toast.error('Failed to compare revisions');
    }
  };

  const handleRestore = async () => {
    if (!restoreVersion) return;
    try {
      await restorePolicyVersion(api, selectedPolicy.id, restoreVersion, restoreReason);
      toast.success(`Policy restored to version ${restoreVersion}`);
      setShowRestoreDialog(false);
      setRestoreVersion(null);
      setRestoreReason('');
      // Refresh
      const response = await getPolicy(api, selectedPolicy.id);
      setSelectedPolicy(response.data);
      fetchRevisions(selectedPolicy.id);
    } catch (error) {
      console.error('Failed to restore policy:', error);
      toast.error('Failed to restore policy');
    }
  };

  const toggleRevisionSelection = (version) => {
    setSelectedRevisions(prev => {
      if (prev.includes(version)) {
        return prev.filter(v => v !== version);
      } else if (prev.length < 2) {
        return [...prev, version];
      }
      return prev;
    });
  };

  const filteredPolicies = policies.filter(p => 
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.policy_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // View Mode
  if (mode === 'view' && selectedPolicy) {
    const policy = selectedPolicy;
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setMode('list'); setSelectedPolicy(null); }} className="text-slate-400 hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white font-barlow">{policy.policy_number}</h1>
                <Badge className={`${STATUS_COLORS[policy.status]} border`}>
                  {t(`policies.statuses.${policy.status}`)}
                </Badge>
                <Badge variant="outline" className="border-slate-600 text-slate-400">
                  v{policy.version}
                </Badge>
              </div>
              <p className="text-slate-400 mt-1">{policy.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => handleEdit(policy)} className="bg-sky-600 hover:bg-sky-700">
              <Edit className="w-4 h-4 mr-2" />Edit
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800/50">
            <TabsTrigger value="content" className="data-[state=active]:bg-slate-700">
              <FileText className="w-4 h-4 mr-2" />Content
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-slate-700">
              <History className="w-4 h-4 mr-2" />Revision History
              {revisions.length > 0 && (
                <Badge variant="secondary" className="ml-2 bg-cyan-500/20 text-cyan-400">{revisions.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="bg-slate-900/60 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white">{policy.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert max-w-none">
                      <p className="text-slate-300 whitespace-pre-wrap">{policy.content}</p>
                    </div>
                    {policy.regulatory_references?.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-slate-800">
                        <h4 className="text-sm font-medium text-slate-400 mb-2">Regulatory References</h4>
                        <div className="flex flex-wrap gap-2">
                          {policy.regulatory_references.map((ref, i) => (
                            <Badge key={i} variant="outline" className="border-slate-600 text-slate-400">{ref}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <Card className="bg-slate-900/60 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">Category</p>
                        <p className="text-white capitalize">{policy.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <History className="w-4 h-4 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">Version</p>
                        <p className="text-white">v{policy.version} ({policy.revision_count || 0} revisions)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">Created</p>
                        <p className="text-white">{format(new Date(policy.created_at), 'PPP')}</p>
                      </div>
                    </div>
                    {policy.approved_at && (
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <div>
                          <p className="text-xs text-slate-500">Approved</p>
                          <p className="text-white">{format(new Date(policy.approved_at), 'PPP')}</p>
                        </div>
                      </div>
                    )}
                    {policy.last_revision_reason && (
                      <div className="flex items-center gap-3">
                        <Edit className="w-4 h-4 text-slate-500" />
                        <div>
                          <p className="text-xs text-slate-500">Last Change</p>
                          <p className="text-slate-300 text-sm">{policy.last_revision_reason}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Status Actions */}
                <Card className="bg-slate-900/60 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {policy.status === 'draft' && (
                      <Button onClick={() => handleStatusChange('pending_approval')} className="w-full bg-yellow-600 hover:bg-yellow-700">
                        Submit for Approval
                      </Button>
                    )}
                    {policy.status === 'pending_approval' && (
                      <>
                        <Button onClick={() => handleStatusChange('approved')} className="w-full bg-emerald-600 hover:bg-emerald-700">
                          <CheckCircle className="w-4 h-4 mr-2" />Approve
                        </Button>
                        <Button onClick={() => handleStatusChange('draft')} variant="outline" className="w-full border-slate-700 text-slate-300">
                          Return to Draft
                        </Button>
                      </>
                    )}
                    {policy.status === 'approved' && (
                      <Button onClick={() => handleStatusChange('archived')} variant="outline" className="w-full border-slate-700 text-slate-300">
                        Archive Policy
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Revision History Tab */}
          <TabsContent value="history" className="mt-6">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-cyan-400" />
                    Revision History
                  </CardTitle>
                  <div className="flex gap-2">
                    {selectedRevisions.length === 2 && (
                      <Button onClick={handleCompare} className="bg-cyan-600 hover:bg-cyan-700">
                        <GitCompare className="w-4 h-4 mr-2" />Compare Selected
                      </Button>
                    )}
                    {selectedRevisions.length > 0 && (
                      <Button variant="outline" onClick={() => setSelectedRevisions([])} className="border-slate-700 text-slate-300">
                        Clear Selection
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {revisions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No revision history available</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {revisions.map((rev, idx) => (
                      <div 
                        key={rev.id} 
                        className={`p-4 rounded-lg border ${selectedRevisions.includes(rev.version) ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/30'} cursor-pointer transition-colors`}
                        onClick={() => toggleRevisionSelection(rev.version)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${CHANGE_TYPE_COLORS[rev.change_type]}`}>
                              v{rev.version}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className={CHANGE_TYPE_COLORS[rev.change_type]}>
                                  {rev.change_type.replace('_', ' ')}
                                </Badge>
                                {idx === 0 && (
                                  <Badge className="bg-cyan-500/20 text-cyan-400">Current</Badge>
                                )}
                              </div>
                              {rev.change_reason && (
                                <p className="text-slate-300 text-sm mt-1">{rev.change_reason}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />{rev.created_by_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />{format(new Date(rev.created_at), 'PPp')}
                                </span>
                              </div>
                              {rev.changed_fields?.length > 0 && (
                                <div className="flex gap-1 mt-2">
                                  {rev.changed_fields.map((field, i) => (
                                    <Badge key={i} variant="outline" className="text-xs border-slate-600 text-slate-400">
                                      {field}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {idx > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => { e.stopPropagation(); setRestoreVersion(rev.version); setShowRestoreDialog(true); }}
                              className="text-slate-400 hover:text-cyan-400"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Compare Dialog */}
        <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <GitCompare className="w-5 h-5 text-cyan-400" />
                Compare Version {comparisonResult?.version_from} to Version {comparisonResult?.version_to}
              </DialogTitle>
            </DialogHeader>
            {comparisonResult && (
              <div className="space-y-4">
                {comparisonResult.changes.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">No differences found</p>
                ) : (
                  comparisonResult.changes.map((change, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={change.change_type === 'added' ? 'bg-green-500/20 text-green-400' : change.change_type === 'removed' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {change.change_type}
                        </Badge>
                        <span className="text-slate-400 font-medium">{change.field}</span>
                      </div>
                      {change.diff ? (
                        <div className="font-mono text-sm space-y-1 mt-2">
                          {change.diff.map((line, i) => (
                            <div key={i} className={`px-2 py-0.5 rounded ${line.type === 'added' ? 'bg-green-900/30 text-green-400' : line.type === 'removed' ? 'bg-red-900/30 text-red-400' : 'text-slate-400'}`}>
                              {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '} {line.content}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <div className="p-2 rounded bg-red-900/20 border border-red-900/50">
                            <p className="text-xs text-red-400 mb-1">From:</p>
                            <p className="text-slate-300 text-sm">{String(change.from_value || '(empty)')}</p>
                          </div>
                          <div className="p-2 rounded bg-green-900/20 border border-green-900/50">
                            <p className="text-xs text-green-400 mb-1">To:</p>
                            <p className="text-slate-300 text-sm">{String(change.to_value || '(empty)')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompareDialog(false)} className="border-slate-700 text-slate-300">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restore Dialog */}
        <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-cyan-400" />
                Restore to Version {restoreVersion}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-yellow-900/20 border border-yellow-900/50">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  <div>
                    <p className="text-yellow-400 font-medium">Warning</p>
                    <p className="text-slate-300 text-sm">This will restore the policy content to version {restoreVersion}. The status will be reset to "draft" and a new revision will be created.</p>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-slate-400">Reason for restore (optional)</Label>
                <Textarea 
                  value={restoreReason} 
                  onChange={(e) => setRestoreReason(e.target.value)}
                  placeholder="Explain why you're restoring this version..."
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRestoreDialog(false); setRestoreVersion(null); setRestoreReason(''); }} className="border-slate-700 text-slate-300">Cancel</Button>
              <Button onClick={handleRestore} className="bg-cyan-600 hover:bg-cyan-700">
                <RotateCcw className="w-4 h-4 mr-2" />Restore Version
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Create/Edit Form
  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => { setMode('list'); setSelectedPolicy(null); }} className="text-slate-400 hover:text-white">
            <ChevronLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <h1 className="text-2xl font-bold text-white font-barlow">
            {mode === 'create' ? t('policies.newPolicy') : `Edit ${selectedPolicy?.policy_number}`}
          </h1>
        </div>
        <Card className="bg-slate-900/60 border-slate-800 max-w-3xl">
          <CardContent className="p-6">
            <form onSubmit={mode === 'create' ? handleCreate : handleUpdate} className="space-y-4">
              <div>
                <Label className="text-slate-300">Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="bg-slate-950 border-slate-800 mt-1" data-testid="policy-title-input" />
              </div>
              <div>
                <Label className="text-slate-300">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="policy">Policy</SelectItem>
                    <SelectItem value="objective">Objective</SelectItem>
                    <SelectItem value="procedure">Procedure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">{t('policies.content')} *</Label>
                <Textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} required rows={10} className="bg-slate-950 border-slate-800 mt-1" data-testid="policy-content-input" />
              </div>
              {mode === 'edit' && (
                <div>
                  <Label className="text-slate-300">Change Reason</Label>
                  <Input 
                    value={formData.change_reason} 
                    onChange={(e) => setFormData({...formData, change_reason: e.target.value})} 
                    placeholder="Describe what changed and why..." 
                    className="bg-slate-950 border-slate-800 mt-1" 
                  />
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-policy-btn">
                  {mode === 'create' ? t('common.create') : 'Save Changes'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setMode('list'); setSelectedPolicy(null); }} className="border-slate-700 text-slate-300">{t('common.cancel')}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List Mode
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="policies-title">{t('policies.title')}</h1>
          <p className="text-slate-400 mt-1">Manage safety policies and objectives</p>
        </div>
        <Button onClick={() => setMode('create')} className="bg-sky-600 hover:bg-sky-700" data-testid="new-policy-btn">
          <Plus className="w-4 h-4 mr-2" />{t('policies.newPolicy')}
        </Button>
      </div>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Search policies..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-950 border-slate-800" />
          </div>
        </CardContent>
      </Card>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-0">
          {loading ? <div className="flex items-center justify-center py-12"><div className="spinner w-8 h-8" /></div> : filteredPolicies.length === 0 ? (
            <div className="text-center py-12 text-slate-500"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>{t('common.noData')}</p></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">{t('policies.policyNumber')}</TableHead>
                  <TableHead className="text-slate-400">Title</TableHead>
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">{t('common.status')}</TableHead>
                  <TableHead className="text-slate-400">{t('policies.version')}</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPolicies.map((p) => (
                  <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/50">
                    <TableCell className="font-mono text-sky-400">{p.policy_number}</TableCell>
                    <TableCell className="text-white max-w-[200px] truncate">{p.title}</TableCell>
                    <TableCell className="text-slate-300 capitalize">{p.category}</TableCell>
                    <TableCell><Badge className={`${STATUS_COLORS[p.status]} border`}>{t(`policies.statuses.${p.status}`)}</Badge></TableCell>
                    <TableCell className="text-slate-300">v{p.version}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleView(p)} className="text-slate-400 hover:text-white">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(p)} className="text-slate-400 hover:text-white">
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
